import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import i18n from '../../i18';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import Search from '../../components/search';
import CheckboxRound from '../../components/round-checkbox';
import { ButtonContainer, StyledDivider } from './styles';
import { VariableSizeList } from 'react-window';
import { getContent } from '../../utils/formatValue';
import { SearchSelectWrapper } from './SearchSelectWrapper';

const ITEM_HEIGHT = 48;

const SearchSelect = ({
    open,
    onClose,
    items,
    title,
    primaryLabel,
    secondaryLabel,
    primaryType,
    secondaryType,
    buttonRef,
    multiSelect = false,
    searchTerm,
    handleSearchChange,
    totalCount,
    allItems,
    selectedValue,
    saveFullData
}) => {
    const [selectedItems, setSelectedItems] = React.useState(selectedValue);
    const clearData = () => {
        handleSearchChange('');
        setSelectedItems([]);
    };
    const handleItemSelect = useCallback(
        (item) => {
            if (multiSelect) {
                const newValue = selectedItems.includes(item)
                    ? selectedItems.filter((v) => v !== item)
                    : [...selectedItems, item];
                setSelectedItems(newValue);
            } else {
                setSelectedItems(item);
            }
        },
        [selectedItems]
    );

    const handleDone = () => {
        if (multiSelect) {
            saveFullData
                ? onClose(selectedItems)
                : onClose(selectedItems.map((i) => i.id));
        } else {
            saveFullData
                ? onClose(selectedItems)
                : onClose(selectedItems ? selectedItems.id : null);
        }
        clearData();
    };
    const renderRow = useCallback(
        ({ index, style }) => {
            const item = items[index];
            return (
                <ListItem key={item.id} style={style} sx={{ p: 0, pr: 2 }}>
                    <ListItemButton onClick={() => handleItemSelect(item)}>
                        <ListItemIcon sx={{ minWidth: '30px' }}>
                            <CheckboxRound
                                checked={
                                    // selectedItems.some((i) => i.id === item.id)
                                    multiSelect
                                        ? selectedItems.some(
                                            (i) => i.id === item.id
                                        )
                                        : selectedItems?.id === item.id
                                }
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary={getContent(
                                item,
                                primaryLabel,
                                primaryType
                            )}
                            secondary={
                                secondaryLabel
                                    ? getContent(
                                        item,
                                        secondaryLabel,
                                        secondaryType
                                    )
                                    : null
                            }
                        />
                    </ListItemButton>
                </ListItem>
            );
        },
        [
            items,
            multiSelect,
            selectedItems,
            handleItemSelect,
            primaryLabel,
            secondaryLabel
        ]
    );

    const itemHeight = secondaryLabel ? ITEM_HEIGHT * 1.6 : ITEM_HEIGHT;
    const listHeight = useMemo(() => {
        const itemCount = items.length;
        const maxHeight = 200;
        const computedHeight = itemCount * itemHeight;

        return itemCount > Math.floor(maxHeight / itemHeight)
            ? maxHeight
            : computedHeight;
    }, [items]);

    const getItemHeight = (index) => {
        const item = items[index];
        return secondaryLabel && item[secondaryLabel]
            ? ITEM_HEIGHT * 1.6
            : ITEM_HEIGHT;
    };

    if (!open) {
        return null;
    }

    return (
        <SearchSelectWrapper
            items={items}
            totalCount={totalCount}
            title={title}
            buttonRef={buttonRef}
            open={open}
            onClose={onClose}
            clearData={clearData}
        >
            <StyledDivider />
            <Search
                searchValue={searchTerm}
                handleSearchData={handleSearchChange}
                sx={{ mt: 1, mb: 1 }}
            />
            <StyledDivider />
            <VariableSizeList
                height={listHeight}
                width="100%"
                itemCount={items.length}
                itemSize={getItemHeight}
                overscanCount={5}
            >
                {renderRow}
            </VariableSizeList>

            <StyledDivider />
            <ButtonContainer>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                        onClose(null);
                        clearData();
                    }}
                >
                    {i18n.t('buttons.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDone}
                >
                    {i18n.t('buttons.done')}
                </Button>
            </ButtonContainer>
        </SearchSelectWrapper>
    );
};

SearchSelect.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    items: PropTypes.arrayOf(PropTypes.any).isRequired,
    title: PropTypes.string.isRequired,
    primaryLabel: PropTypes.string.isRequired,
    secondaryLabel: PropTypes.string,
    buttonRef: PropTypes.object.isRequired,
    multiSelect: PropTypes.bool,
    selectedValue: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.array
    ])
};

export default React.memo(SearchSelect);
